import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import { formatApiErrorDetail } from "@/lib/authForms";

/**
 * /verify-email?token=... — consumes the token from the email link.
 * On success the backend mints a session cookie, so we just refresh()
 * the auth context and redirect to /app.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState(token ? "loading" : "missing"); // loading | success | error | missing
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current || !token) return;
    ran.current = true;
    (async () => {
      try {
        await axios.post(`${API}/auth/verify-email`, { token });
        await refresh();
        setState("success");
        setTimeout(() => navigate("/app", { replace: true }), 1500);
      } catch (err) {
        setError(formatApiErrorDetail(err?.response?.data?.detail, "Verification failed."));
        setState("error");
      }
    })();
  }, [token, refresh, navigate]);

  const content = {
    loading: (
      <div className="mt-8 text-center py-6">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto" />
        <p className="mt-4 text-sm text-white/70">Verifying your email…</p>
      </div>
    ),
    success: (
      <div className="mt-8 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-6 text-center" data-testid="verify-success">
        <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
        <p className="font-display text-xl text-white">Email verified.</p>
        <p className="text-sm text-white/65 mt-2">Taking you to the studio…</p>
      </div>
    ),
    error: (
      <div className="mt-8 rounded-2xl border border-red-500/25 bg-red-500/5 p-6 text-center" data-testid="verify-error">
        <XCircle className="w-10 h-10 text-red-300 mx-auto mb-3" />
        <p className="font-display text-xl text-white">Couldn't verify</p>
        <p className="text-sm text-white/65 mt-2 leading-relaxed">{error}</p>
        <Link to="/signin" className="inline-block mt-5 text-xs text-[#D4AF37] hover:text-[#F3CE5A]">
          Back to sign in →
        </Link>
      </div>
    ),
    missing: (
      <div className="mt-8 rounded-2xl border border-white/10 p-6 text-center" data-testid="verify-missing">
        <p className="text-sm text-white/70">This page expects a verification link from your inbox.</p>
        <Link to="/signin" className="inline-block mt-4 text-xs text-[#D4AF37] hover:text-[#F3CE5A]">
          Back to sign in →
        </Link>
      </div>
    ),
  };

  return (
    <AuthShell title="Verify email" subtitle="Activating your studio access.">
      {content[state]}
    </AuthShell>
  );
}
